'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react'
import type { Swiper as SwiperClass } from 'swiper'
import { getNewsDisplayTitle } from '@/lib/news-display'
import { getNewsHref } from '@/lib/news-url'
import NewsCategoryTag from './NewsCategoryTag'
import { GalleryCountBadge } from './media/MediaThumbnailOverlay'
import LiveBadge, { isLiveNews } from './LiveBadge'
import { resolveSliderAccent } from '@/lib/category-accent'
import type { News } from '@/lib/types'
import { normalizeMediaPath } from '@/lib/utils'
import {
  mastheadDefaultSrc,
  mastheadDesktopSrcSet,
  mastheadMobileSrcSet,
} from '@/lib/cdn-srcset'
import { isLabPerformanceAgent } from '@/lib/defer-third-party'

export type MainSliderNews = News & { imageUrl?: string | null }

export type MainSliderSize = 'main' | 'compact'
export type MainSliderAccent = 'red' | 'green'
export type MainSliderPagination = 'dots' | 'bars'

interface MainSliderProps {
  news: MainSliderNews[]
  size?: MainSliderSize
  accent?: MainSliderAccent
  accentColor?: string | null
  pagination?: MainSliderPagination
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
  className?: string
  lcpPriority?: boolean
}

let mainSliderSwiperPromise: ReturnType<typeof importMainSliderSwiper> | null = null

function importMainSliderSwiper() {
  return import('./MainSliderSwiper')
}

const loadMainSliderSwiper = () => {
  mainSliderSwiperPromise ??= importMainSliderSwiper()
  return mainSliderSwiperPromise
}

const MainSliderSwiper = dynamic(loadMainSliderSwiper, { loading: () => null })

function isDedicatedMasthead(item: MainSliderNews) {
  if (normalizeMediaPath(item.sliderImage)) return true
  const path = normalizeMediaPath(item.image)
  if (path.includes('manset/')) return true
  const url = item.imageUrl?.trim() || ''
  return url.includes('/manset/')
}

/** Sadece görsel — başlık stage’de sabit katmanda (Swiper yüklenince kaymasın) */
function MastheadImageOnly({
  item,
  imageUrl,
  isCompact,
  lcpPriority,
}: {
  item: MainSliderNews
  imageUrl: string
  isCompact: boolean
  lcpPriority: boolean
}) {
  const headline = getNewsDisplayTitle(item)
  const href = getNewsHref(item)
  const dedicated = isDedicatedMasthead(item)
  const liveBadge = isLiveNews(item)

  return (
    <div className="relative h-full w-full overflow-hidden">
      <Link href={href} className="absolute inset-0 block overflow-hidden" aria-label={headline}>
        <picture className="absolute inset-0 block h-full w-full">
          <source
            media="(max-width: 767px)"
            srcSet={mastheadMobileSrcSet(imageUrl)}
            sizes="100vw"
          />
          <source
            media="(min-width: 768px)"
            srcSet={mastheadDesktopSrcSet(imageUrl)}
            sizes={
              isCompact
                ? '(max-width: 1023px) 100vw, min(960px, 100vw)'
                : '960px'
            }
          />
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={mastheadDefaultSrc(imageUrl)}
            alt={item.title}
            width={720}
            height={400}
            className="main-slider-image absolute inset-0 h-full w-full object-cover object-center"
            fetchPriority={lcpPriority ? 'high' : 'low'}
            loading={lcpPriority ? 'eager' : 'lazy'}
            decoding="async"
          />
        </picture>
      </Link>
      {!dedicated && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[55%] bg-gradient-to-t from-black/90 via-black/50 to-transparent md:h-[62%] md:from-black/85 md:via-black/40" />
      )}
      {dedicated && liveBadge ? (
        <LiveBadge variant="overlay" className="z-30 sm:!top-4 sm:!left-4" />
      ) : null}
      {item.isType === 1 && (
        <GalleryCountBadge
          count={item._count?.photos || item.photos?.length || 0}
          className="right-2 top-2 z-30 sm:right-4 sm:top-4"
        />
      )}
    </div>
  )
}

/** Stage’de sabit — mobilde tamamen geçirgen (swipe Swiper’a gitsin) */
function MastheadCaptionOverlay({ item }: { item: MainSliderNews }) {
  const dedicated = isDedicatedMasthead(item)
  const headline = getNewsDisplayTitle(item)
  const href = getNewsHref(item)
  const liveBadge = isLiveNews(item)
  // Masaüstünde etiket tıklanır; mobilde pointer yok → yazı üzerinde kaydırma çalışır
  const tagRowClass =
    'flex flex-wrap items-center gap-1.5 pointer-events-none [@media(hover:hover)_and_(pointer:fine)]:pointer-events-auto'

  if (dedicated) {
    return (
      <div className="pointer-events-none absolute inset-0 z-[55]">
        {/* Masaüstü: Swiper tıklamayı yutsa bile manşete tıklanınca haber açılsın */}
        <Link
          href={href}
          className="absolute inset-0 hidden [@media(hover:hover)_and_(pointer:fine)]:block"
          aria-label={headline}
        />
        <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/55 via-black/20 to-transparent p-2.5 sm:p-3">
          <div className={`${tagRowClass} sm:gap-2`}>
            <NewsCategoryTag
              tags={item.tags}
              category={item.category}
              variant="rounded-full"
              surface="dark"
              categoryAsHashtag
            />
            {liveBadge ? <LiveBadge variant="pill" /> : null}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="main-slider-caption pointer-events-none absolute inset-x-0 bottom-0 z-[55] text-white">
      <div className="max-w-4xl min-w-0">
        <div className={`${tagRowClass} mb-1.5 sm:mb-2 sm:gap-2`}>
          <NewsCategoryTag
            tags={item.tags}
            category={item.category}
            variant="rounded-full"
            surface="dark"
            categoryAsHashtag
          />
          {liveBadge ? <LiveBadge variant="pill" /> : null}
        </div>
        <Link href={href} className="pointer-events-auto block">
          <h2 className="main-slider-title line-clamp-3 font-bold text-white md:line-clamp-2">
            {headline}
          </h2>
        </Link>
      </div>
    </div>
  )
}

/**
 * Tek kabuk + sabit caption katmanı.
 * Hover = sadece preload. Title Swiper’dan bağımsız → kayma yok.
 */
export default function MainSlider(props: MainSliderProps) {
  const {
    news,
    size = 'main',
    accent = 'red',
    accentColor,
    pagination = 'dots',
    className = '',
    lcpPriority = false,
    activeIndex: activeIndexProp,
    onActiveIndexChange,
  } = props

  const slides = news.filter((item) => item.imageUrl?.trim())
  const item = slides[0]
  const prevRef = useRef<HTMLButtonElement | null>(null)
  const nextRef = useRef<HTMLButtonElement | null>(null)
  const paginationRef = useRef<HTMLDivElement | null>(null)
  const pendingNavRef = useRef<'prev' | 'next' | null>(null)
  const pendingRevealRef = useRef(false)
  const hoveringRef = useRef(false)
  const hoverArmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const swiperRef = useRef<SwiperClass | null>(null)
  const liveRef = useRef(false)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  const [chunkReady, setChunkReady] = useState(false)
  const [live, setLive] = useState(false)
  const [slideIndex, setSlideIndex] = useState(0)

  liveRef.current = live

  const canFineHover = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches

  /** JS chunk ısıt */
  const warmChunk = useCallback(() => {
    void loadMainSliderSwiper()
  }, [])

  /** Swiper’ı LCP örtüsünün altında mount et (görünmez) */
  const mountSwiper = useCallback(() => {
    void loadMainSliderSwiper().then(() => {
      setChunkReady(true)
    })
  }, [])

  const handleIndexChange = useCallback(
    (index: number) => {
      setSlideIndex(index)
      onActiveIndexChange?.(index)
    },
    [onActiveIndexChange],
  )

  const revealLive = useCallback(
    (swiper: SwiperClass) => {
      if (liveRef.current) return
      const nav = pendingNavRef.current
      pendingNavRef.current = null
      pendingRevealRef.current = false
      if (nav === 'next') swiper.slideNext(0)
      else if (nav === 'prev') swiper.slidePrev(0)
      handleIndexChange(swiper.realIndex)
      // Çift rAF: Swiper paint bitsin, sonra örtüyü kaldır (zıplamasız)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setLive(true))
      })
    },
    [handleIndexChange],
  )

  const handleSwiperReady = useCallback(
    (swiper: SwiperClass) => {
      swiperRef.current = swiper
      // Tık / hâlâ hover’daysa örtüyü kaldır — aksi halde hazır bekler
      if (pendingNavRef.current || (pendingRevealRef.current && hoveringRef.current)) {
        revealLive(swiper)
      } else {
        pendingRevealRef.current = false
      }
    },
    [revealLive],
  )

  /** Masaüstü: manşete gelince hazırla + hazırsa aktif et */
  const armFromHover = useCallback(() => {
    if (liveRef.current || !canFineHover()) return
    hoveringRef.current = true
    warmChunk()

    const swiper = swiperRef.current
    if (swiper && !swiper.destroyed) {
      pendingRevealRef.current = true
      revealLive(swiper)
      return
    }

    if (hoverArmTimerRef.current) clearTimeout(hoverArmTimerRef.current)
    // Kısa gecikme: hızlı geçişte gereksiz mount yok
    hoverArmTimerRef.current = setTimeout(() => {
      if (!hoveringRef.current || liveRef.current) return
      pendingRevealRef.current = true
      mountSwiper()
    }, 120)
  }, [mountSwiper, revealLive, warmChunk])

  const onStagePointerLeave = useCallback(() => {
    hoveringRef.current = false
    if (hoverArmTimerRef.current) {
      clearTimeout(hoverArmTimerRef.current)
      hoverArmTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (activeIndexProp === undefined) return
    setSlideIndex(activeIndexProp)
  }, [activeIndexProp])

  useEffect(() => {
    if (isLabPerformanceAgent()) return

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const arm = () => {
      // Idle’da chunk ısıt; mount hover/tık ile
      timeoutId = setTimeout(() => {
        if (!cancelled) warmChunk()
      }, 8_000)
    }

    if (document.readyState === 'complete') arm()
    else window.addEventListener('load', arm, { once: true })

    return () => {
      cancelled = true
      window.removeEventListener('load', arm)
      if (timeoutId) clearTimeout(timeoutId)
      if (hoverArmTimerRef.current) clearTimeout(hoverArmTimerRef.current)
    }
  }, [warmChunk])

  if (!item?.imageUrl?.trim()) return null

  const imageUrl = item.imageUrl.trim()
  const isCompact = size === 'compact'
  const navIconSize = isCompact ? 14 : 18
  const canLoop = slides.length > 1
  const useBarPagination = pagination === 'bars'
  const showPager = slides.length > 1
  const activeItem = slides[slideIndex] ?? item
  // Accent aktif slaytın kategorisini izlemeli (ilk slayta sabitlenirse
  // Gündem haberi aktifken noktalar önceki kategorinin renginde kalır)
  const sliderAccent = resolveSliderAccent(accentColor, activeItem.category?.color, accent)
  const usePresetGreenClass =
    !accentColor?.trim() && !activeItem.category?.color?.trim() && accent === 'green'

  const reveal = (dir?: 'prev' | 'next') => {
    if (dir) pendingNavRef.current = dir
    pendingRevealRef.current = true
    hoveringRef.current = true
    const swiper = swiperRef.current
    if (swiper && !swiper.destroyed) {
      revealLive(swiper)
      return
    }
    mountSwiper()
  }

  /** Mobil: LCP örtüsü / yazı üstündeyken de yatay kaydırmayı yakala */
  const onStageTouchStart = (e: ReactTouchEvent) => {
    const t = e.changedTouches[0]
    if (!t) return
    touchStartRef.current = { x: t.clientX, y: t.clientY }
    if (!liveRef.current) {
      pendingRevealRef.current = true
      mountSwiper()
    }
  }

  const onStageTouchEnd = (e: ReactTouchEvent) => {
    const start = touchStartRef.current
    touchStartRef.current = null
    if (!start) return

    // Canlı Swiper kendi swipe’ını yönetir — çift kaydırma olmasın
    if (liveRef.current) return

    const t = e.changedTouches[0]
    if (!t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    const absX = Math.abs(dx)
    const absY = Math.abs(dy)
    // ~%4 ekran / min 6px — çok hafif kaydırma da geçsin
    const minSwipe = Math.max(6, Math.round(window.innerWidth * 0.04))

    // Belirgin dikey scroll ise sayfa kaydırmasına bırak
    if (absY > absX * 1.75 && absY > 24) {
      if (!liveRef.current) reveal()
      return
    }

    if (absX < minSwipe) {
      if (!liveRef.current) reveal()
      return
    }

    if (slides.length <= 1) {
      reveal()
      return
    }

    reveal(dx < 0 ? 'next' : 'prev')
  }

  const onNavClick = (dir: 'prev' | 'next') => (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const swiper = swiperRef.current
    if (live && swiper && !swiper.destroyed) {
      if (dir === 'next') swiper.slideNext()
      else swiper.slidePrev()
      return
    }

    reveal(dir)
  }

  const pagerClass = `main-slider-pager${
    useBarPagination ? ' main-slider-pager--bars main-slider-pager--overlay' : ''
  }`

  const swiperPager = showPager ? (
    <div
      ref={paginationRef}
      role="group"
      className={`${pagerClass}${live ? '' : ' pointer-events-none invisible'}`}
      aria-label="Manşet sayfalama"
      aria-hidden={!live}
    />
  ) : null

  const staticPager = showPager && !live ? (
    <div className={pagerClass} aria-hidden onClick={() => reveal()}>
      {slides.map((_, index) => (
        <span
          key={index}
          className={`swiper-pagination-bullet${index === 0 ? ' swiper-pagination-bullet-active' : ''}`}
        >
          {!useBarPagination ? (
            <span className="swiper-pagination-bullet-number">{index + 1}</span>
          ) : null}
        </span>
      ))}
    </div>
  ) : null

  const navButtons = canLoop ? (
    <>
      <button
        ref={prevRef}
        type="button"
        aria-label="Önceki manşet"
        className={`main-slider-nav main-slider-nav-prev ${isCompact ? 'main-slider-nav--compact' : ''}`}
        onFocus={armFromHover}
        onClick={onNavClick('prev')}
      >
        <svg viewBox="0 0 24 24" width={navIconSize} height={navIconSize} aria-hidden="true">
          <path fill="currentColor" d="M15.5 19L8.5 12l7-7 1.4 1.4L11.3 12l5.6 5.6z" />
        </svg>
      </button>
      <button
        ref={nextRef}
        type="button"
        aria-label="Sonraki manşet"
        className={`main-slider-nav main-slider-nav-next ${isCompact ? 'main-slider-nav--compact' : ''}`}
        onFocus={armFromHover}
        onClick={onNavClick('next')}
      >
        <svg viewBox="0 0 24 24" width={navIconSize} height={navIconSize} aria-hidden="true">
          <path fill="currentColor" d="M8.5 19l-1.4-1.4 5.6-5.6-5.6-5.6L8.5 5l7 7z" />
        </svg>
      </button>
    </>
  ) : null

  return (
    <div
      className={`main-slider-wrap w-full ${isCompact ? 'main-slider-wrap--compact' : ''} ${
        usePresetGreenClass ? 'main-slider-wrap--accent-green' : ''
      } ${className}`}
      style={{ ['--slider-accent' as string]: sliderAccent }}
    >
      <div
        className={`main-slider-stage relative w-full overflow-hidden rounded-[5px] border border-gray-200/80 dark:border-slate-700 touch-pan-y ${
          isCompact ? '' : 'md:h-[450px] md:pb-0'
        }`}
        onPointerEnter={armFromHover}
        onPointerLeave={onStagePointerLeave}
        onTouchStart={onStageTouchStart}
        onTouchEnd={onStageTouchEnd}
      >
        {useBarPagination ? (
          <>
            {swiperPager}
            {staticPager}
          </>
        ) : null}

        {chunkReady ? (
          <div
            className={
              live
                ? 'absolute inset-0 z-[1] h-full w-full'
                : 'pointer-events-none absolute inset-0 z-[1] h-full w-full opacity-0'
            }
            aria-hidden={!live}
          >
            <MainSliderSwiper
              {...props}
              embedded
              imageOnly
              prevElRef={prevRef}
              nextElRef={nextRef}
              paginationElRef={paginationRef}
              forceBarPagination={useBarPagination}
              activeIndex={activeIndexProp}
              onActiveIndexChange={handleIndexChange}
              onReady={handleSwiperReady}
            />
          </div>
        ) : null}

        {!live ? (
          <div className="absolute inset-0 z-[50]" data-slider-lcp-cover="1">
            <MastheadImageOnly
              item={item}
              imageUrl={imageUrl}
              isCompact={isCompact}
              lcpPriority={lcpPriority}
            />
            {useBarPagination && showPager ? (
              <div
                className="main-slider-pager main-slider-pager--bars main-slider-pager--overlay"
                aria-hidden
                onClick={() => reveal()}
              >
                {slides.map((_, index) => (
                  <span
                    key={index}
                    className={`swiper-pagination-bullet${index === 0 ? ' swiper-pagination-bullet-active' : ''}`}
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Sabit başlık — Swiper yüklenince remount olmaz */}
        <MastheadCaptionOverlay item={activeItem} />

        {navButtons}
      </div>

      {!useBarPagination ? (
        <>
          {swiperPager}
          {staticPager}
        </>
      ) : null}
    </div>
  )
}
